import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Trash2, Eye, ArrowUpDown, ArrowUp, ArrowDown, Ban, CheckCircle, Gift } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useState, useMemo } from "react";

interface TenantListTableProps {
  tenants: any[];
  isLoading: boolean;
  onDelete: (id: string) => void;
  onInactivate: (id: string) => void;
  onActivate: (id: string) => void;
  onViewDetails: (tenant: any) => void;
  onBonus?: (tenant: any) => void;
}

export function TenantListTable({ tenants, isLoading, onDelete, onInactivate, onActivate, onViewDetails, onBonus }: TenantListTableProps) {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  const sortedTenants = useMemo(() => {
    if (!sortConfig) return tenants;

    const sorted = [...tenants].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortConfig.key) {
      case 'name':
        aValue = a.name || '';
        bValue = b.name || '';
        break;
      case 'plan':
          aValue = a.subscription_plans?.name || '';
          bValue = b.subscription_plans?.name || '';
          break;
        case 'status':
          aValue = a.is_active ? 1 : 0;
          bValue = b.is_active ? 1 : 0;
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'userCount':
          aValue = a.userCount || 0;
          bValue = b.userCount || 0;
          break;
        case 'last_access':
          aValue = a.adminInfo?.last_access_at ? new Date(a.adminInfo.last_access_at).getTime() : 0;
          bValue = b.adminInfo?.last_access_at ? new Date(b.adminInfo.last_access_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [tenants, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig((current) => {
      if (!current || current.key !== key) {
        return { key, direction: 'asc' };
      }
      if (current.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      return null;
    });
  };

  const getSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="h-4 w-4 ml-1" />
    ) : (
      <ArrowDown className="h-4 w-4 ml-1" />
    );
  };

  if (isLoading) {
    return <div className="text-center py-8">Carregando...</div>;
  }

  if (tenants.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Nenhuma tenant encontrada
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead 
              className="cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => handleSort('name')}
            >
              <div className="flex items-center">
                Empresa
                {getSortIcon('name')}
              </div>
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => handleSort('plan')}
            >
              <div className="flex items-center">
                Plano
                {getSortIcon('plan')}
              </div>
            </TableHead>
            <TableHead>Bonificação</TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => handleSort('created_at')}
            >
              <div className="flex items-center">
                Criado em
                {getSortIcon('created_at')}
              </div>
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-accent/50 transition-colors text-center"
              onClick={() => handleSort('userCount')}
            >
              <div className="flex items-center justify-center">
                Nº Usuários
                {getSortIcon('userCount')}
              </div>
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => handleSort('last_access')}
            >
              <div className="flex items-center">
                Último Acesso
                {getSortIcon('last_access')}
              </div>
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => handleSort('status')}
            >
              <div className="flex items-center">
                Status
                {getSortIcon('status')}
              </div>
            </TableHead>
            <TableHead className="text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedTenants.map((tenant) => {
            return (
              <TableRow key={tenant.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    {tenant.name}
                    {tenant.is_demo && <Badge variant="secondary">DEMO</Badge>}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {tenant.subscription_plan_id ? (
                      <div>
                        <div className="font-medium">{tenant.subscription_plans?.name}</div>
                        {tenant.subscription_plans?.price_monthly && (
                          <div className="text-xs text-muted-foreground">
                            R$ {tenant.subscription_plans.price_monthly}/mês
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Sem plano</span>
                        <Badge variant="destructive">⚠️</Badge>
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {tenant.has_active_bonus && (
                    <Badge variant="default" className="bg-purple-600">
                      <Gift className="h-3 w-3 mr-1" />
                      Ativa
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {format(new Date(tenant.created_at), "dd/MM/yyyy", { locale: ptBR })}
                </TableCell>
                <TableCell className="text-center">
                  <div className="font-medium">{tenant.userCount || 0}</div>
                </TableCell>
                <TableCell>
                  {tenant.adminInfo?.last_access_at ? (
                    <div className="text-sm">
                      {format(new Date(tenant.adminInfo.last_access_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                    </div>
                  ) : (
                    <span className="text-muted-foreground text-sm">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={tenant.is_active ? "default" : "secondary"}>
                    {tenant.is_active ? "Ativo" : "Inativo"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      title="Ver detalhes"
                      onClick={() => onViewDetails(tenant)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {onBonus && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onBonus(tenant)}
                        title="Bonificar plano"
                      >
                        <Gift className="h-4 w-4" />
                      </Button>
                    )}
                    {tenant.is_demo && tenant.is_active && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onInactivate(tenant.id)}
                        title="Inativar tenant demo"
                      >
                        <Ban className="h-4 w-4" />
                      </Button>
                    )}
                    {tenant.is_demo && !tenant.is_active && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onActivate(tenant.id)}
                        title="Ativar tenant demo"
                      >
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    )}
                    {tenant.is_demo && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDelete(tenant.id)}
                        title="Deletar tenant demo"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
